/**
 * MyCompensationTab Component
 *
 * Displays LO-specific compensation information for a loan.
 * Visible to loan officers and admins only.
 *
 * Features:
 * - LO Assignment dropdown (admin-only editable)
 * - Compensation calculation based on banked plan or points above par
 * - Lender Credit display (read-only, managed from Product & Pricing)
 * - Active concession points factored into pointsAbovePar
 * - Net to LO summary
 */

import { useState, useEffect, useCallback, useMemo } from 'react';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import { Separator } from '@/components/ui/separator';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue
} from '@/components/ui/select';
import { Loader2, Wallet, User, TrendingUp } from 'lucide-react';
import { cn } from '@/lib/utils';
import { toast } from 'sonner';
import { createComponentLogger } from '@/utils/logger';
import { useRoleAccess } from '@/hooks/useRoleAccess';
import type { ProspectLoan } from './types';
import { lenderCreditBreakdown } from './utils/loanValueHelpers';
import {
  getOrganizationLoanOfficers,
  getLoanPricingItems,
  bpsToDollars,
  formatCurrency,
  formatPoints,
  type LOUserInfo,
  type LoanPricingItem
} from '@/services/loanPricingService';
import { fetchConcessions, type LendwiseConcession } from '@/services/lendwiseConcessionService';
import { extractEncompassLoCompBps } from '@/utils/loCompDerived';
import { supabase } from '@/utils/supabase';

const log = createComponentLogger('MyCompensationTab', 'encompass-prospects');

interface MyCompensationTabProps {
  loan: ProspectLoan;
  onLoanUpdate?: (updates: Partial<ProspectLoan>) => void;
}

export function MyCompensationTab({ loan, onLoanUpdate }: MyCompensationTabProps) {
  // Role access
  const { getCurrentUser } = useRoleAccess();
  const currentUser = getCurrentUser();
  const canReassignLoanOfficer = currentUser?.roles?.some((role) =>
    role === 'admin' || role === 'manager'
  ) ?? false;

  // LO state
  const [loanOfficers, setLoanOfficers] = useState<LOUserInfo[]>([]);
  const [selectedLOId, setSelectedLOId] = useState<string>('');
  const [selectedLO, setSelectedLO] = useState<LOUserInfo | null>(null);
  const [isLoadingLOs, setIsLoadingLOs] = useState(true);
  const [isSavingLO, setIsSavingLO] = useState(false);

  // Admin-entered LO compensation from pricing items
  const [adminLoComp, setAdminLoComp] = useState<LoanPricingItem | null>(null);

  // Active Lendwise concessions (for concession-adjusted pointsAbovePar)
  const [activeConcessionPoints, setActiveConcessionPoints] = useState(0);

  // Lender credit — read-only total issued on the loan. Aggregates field 4794
  // (Non-Specific Lender Credit) + LENPCC (Lender-Paid Closing Costs). Both are
  // additive sources — reading only 4794 would undercount any credit already
  // allocated to specific closing-cost line items. See BankerRecon.tsx:917-956
  // for the canonical precedent.
  const lenderCreditDollars = lenderCreditBreakdown(loan).total;

  // Get loan amount
  const loanAmount = loan.rate_lock_base_loan_amount || loan.trans_details_loan_amt || 0;
  const totalBuyPrice = loan.rate_lock_total_buy_price || 0;
  const pointsAbovePar = totalBuyPrice > 0 ? (totalBuyPrice + activeConcessionPoints) - 100 : 0;

  // Load loan officers
  const loadLoanOfficers = useCallback(async () => {
    try {
      setIsLoadingLOs(true);
      const officers = await getOrganizationLoanOfficers();
      setLoanOfficers(officers);

      // Find current LO from loan
      if (loan.loan_officer_id) {
        const currentLO = officers.find(
          lo => lo.encompassUserId === loan.loan_officer_id || lo.id === loan.loan_officer_id
        );
        if (currentLO) {
          setSelectedLOId(currentLO.id);
          setSelectedLO(currentLO);
        }
      }
    } catch (error) {
      log.error('Failed to load loan officers', {}, error as Error);
    } finally {
      setIsLoadingLOs(false);
    }
  }, [loan.loan_officer_id]);

  // Load data on mount
  useEffect(() => {
    loadLoanOfficers();
  }, [loadLoanOfficers]);

  // Load active concession points for concession-adjusted pointsAbovePar
  useEffect(() => {
    let cancelled = false;
    fetchConcessions(loan.id)
      .then((data) => {
        if (!cancelled) {
          const activePoints = data
            .filter((c: LendwiseConcession) => !c.is_legacy)
            .reduce((sum: number, c: LendwiseConcession) => sum + c.amount_points, 0);
          setActiveConcessionPoints(activePoints);
        }
      })
      .catch(() => { /* non-critical */ });
    return () => {
 cancelled = true; 
};
  }, [loan.id]);

  // Load admin-entered LO compensation pricing item
  useEffect(() => {
    async function loadAdminLoComp() {
      if (!loan.encompass_loan_id) {
return;
}
      try {
        const items = await getLoanPricingItems(loan.encompass_loan_id);
        const loCompItem = items.find(item => item.itemType === 'lo_compensation');
        setAdminLoComp(loCompItem || null);
      } catch (error) {
        log.error('Failed to load admin LO compensation', {}, error as Error);
      }
    }
    loadAdminLoComp();
  }, [loan.encompass_loan_id]);

  // Handle LO selection change (admin only)
  const handleLOChange = useCallback(async (loId: string) => {
    const newLO = loanOfficers.find(lo => lo.id === loId);
    if (!newLO) {
      return;
    }

    setSelectedLOId(loId);
    setSelectedLO(newLO);

    try {
      setIsSavingLO(true);

      const { data, error } = await supabase.rpc(
        'admin_reassign_primary_loan_officer',
        {
          p_loan_id: loan.id,
          p_target_user_id: newLO.id
        }
      );

      if (error) {
        throw error;
      }

      const updated = data as {
        loan_officer_id?: string;
        loan_officer_first_name?: string;
        loan_officer_last_name?: string;
      } | null;
      const [fallbackFirstName = '', ...fallbackLastNameParts] =
        newLO.fullName.split(' ');
      toast.success(`Loan officer updated to ${newLO.fullName}`);
      onLoanUpdate?.({
        loan_officer_id:
          updated?.loan_officer_id ?? newLO.encompassUserId ?? newLO.id,
        loan_officer_first_name:
          updated?.loan_officer_first_name ?? fallbackFirstName,
        loan_officer_last_name:
          updated?.loan_officer_last_name
          ?? fallbackLastNameParts.join(' ')
      });
    } catch (error) {
      log.error('Failed to update loan officer', {}, error as Error);
      toast.error('Failed to update loan officer');
      // Revert selection
      if (loan.loan_officer_id) {
        const originalLO = loanOfficers.find(
          lo => lo.encompassUserId === loan.loan_officer_id || lo.id === loan.loan_officer_id
        );
        if (originalLO) {
          setSelectedLOId(originalLO.id);
          setSelectedLO(originalLO);
        }
      }
    } finally {
      setIsSavingLO(false);
    }
  }, [loan.id, loan.loan_officer_id, loanOfficers, onLoanUpdate]);

  // Encompass LO Compensation base — the source of truth
  const encompassLoCompBps = useMemo(
    () => extractEncompassLoCompBps(loan.rate_lock_buy_side_adjustments),
    [loan.rate_lock_buy_side_adjustments]
  );

  // Calculate compensation
  const compensation = useMemo(() => {
    let loCommissionPercent: number;
    let loCommissionDollars: number;
    let planType: 'banked' | 'points_above_par' | 'admin_entered';

    if (encompassLoCompBps > 0) {
      // Encompass LO Compensation margin row exists — use as primary source
      // Apply reduction from local metadata row if present
      planType = 'admin_entered';
      const storedReduction = adminLoComp?.reductionBps ?? 0;
      const appliedReduction = Math.min(storedReduction, encompassLoCompBps);
      const effectiveBps = encompassLoCompBps - appliedReduction;
      loCommissionPercent = effectiveBps / 100;
      loCommissionDollars = bpsToDollars(effectiveBps, loanAmount);
    } else if (selectedLO?.bankedCompensation) {
      // No Encompass LO comp — use banked compensation rate
      planType = 'banked';
      loCommissionPercent = selectedLO.bankedCompensation;
      loCommissionDollars = (loCommissionPercent / 100) * loanAmount;
    } else {
      // Use points above par (full margin goes to LO)
      planType = 'points_above_par';
      loCommissionPercent = pointsAbovePar;
      loCommissionDollars = (pointsAbovePar / 100) * loanAmount;
    }

    // Net to LO == post-LOCR effective commission. Do NOT subtract lender
    // credits here — the LOCR (applied above via `appliedReduction`) already
    // released value out of the LO's gross comp into the lender-credit
    // availability pool. Subtracting lender credits again would double-count
    // against the LO. Any unused availability flows to company profitability,
    // not back to the LO. The `lenderCreditDollars` value is rendered below
    // for informational context only.
    const netToLO = loCommissionDollars;

    return {
      planType,
      loCommissionPercent,
      loCommissionDollars,
      netToLO
    };
  }, [selectedLO, loanAmount, pointsAbovePar, adminLoComp, encompassLoCompBps]);

  // Loading state
  if (isLoadingLOs) {
    return (
      <div className="flex items-center justify-center py-12">
        <Loader2 className="h-6 w-6 animate-spin text-muted-foreground" />
      </div>
    );
  }

  const currentLOName = selectedLO
    ? selectedLO.fullName
    : [loan.loan_officer_first_name, loan.loan_officer_last_name].filter(Boolean).join(' ') || 'Unassigned';

  return (
    <div className="space-y-6">
      {/* LO Assignment Card */}
      <Card>
        <CardHeader className="pb-3">
          <CardTitle className="text-base flex items-center gap-2">
            <User className="h-4 w-4" />
            Loan Officer Assignment
          </CardTitle>
        </CardHeader>
        <CardContent className="space-y-4">
          <div className="space-y-2">
            <div className="text-xs text-muted-foreground uppercase tracking-wide">
              Assigned Loan Officer
            </div>
            {canReassignLoanOfficer ? (
              <Select
                value={selectedLOId}
                onValueChange={handleLOChange}
                disabled={isSavingLO}
              >
                <SelectTrigger className="w-full">
                  <SelectValue placeholder="Select loan officer..." />
                </SelectTrigger>
                <SelectContent>
                  {loanOfficers.map((lo) => (
                    <SelectItem key={lo.id} value={lo.id}>
                      <div className="flex items-center gap-2">
                        <span>{lo.fullName}</span>
                        {lo.bankedCompensation && (
                          <Badge variant="outline" className="text-xs">
                            Banked @ {lo.bankedCompensation}%
                          </Badge>
                        )}
                      </div>
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            ) : (
              <div className="flex items-center gap-2 py-2">
                <span className="font-medium">{currentLOName}</span>
                {selectedLO?.bankedCompensation && (
                  <Badge variant="outline" className="text-xs">
                    Banked @ {selectedLO.bankedCompensation}%
                  </Badge>
                )}
              </div>
            )}
            {isSavingLO && (
              <div className="flex items-center gap-2 text-sm text-muted-foreground">
                <Loader2 className="h-3 w-3 animate-spin" />
                Updating...
              </div>
            )}
          </div>

          {/* Compensation Plan Info */}
          <div className="bg-muted/50 rounded-lg p-3">
            <div className="text-xs text-muted-foreground uppercase tracking-wide mb-1">
              Compensation Plan
            </div>
            <div className="text-sm font-medium">
              {compensation.planType === 'admin_entered' ? (
                <>Encompass Lock: {compensation.loCommissionPercent.toFixed(3)}% = {formatCurrency(compensation.loCommissionDollars)}</>
              ) : (selectedLO?.optimalBlueCurrentPercentage ?? selectedLO?.bankedCompensation ?? null) !== null ? (
                <>Plan: {selectedLO?.optimalBlueCurrentPercentage ?? selectedLO?.bankedCompensation}%</>
              ) : (
                <>No comp plan (earns points above par)</>
              )}
            </div>
          </div>
        </CardContent>
      </Card>

      <Separator />

      {/* Compensation Breakdown Card */}
      <Card>
        <CardHeader className="pb-3">
          <CardTitle className="text-base flex items-center gap-2">
            <TrendingUp className="h-4 w-4" />
            Compensation Breakdown
          </CardTitle>
        </CardHeader>
        <CardContent className="space-y-4">
          {/* Loan Details */}
          <div className="grid grid-cols-2 gap-4">
            <div>
              <div className="text-xs text-muted-foreground uppercase tracking-wide">
                Loan Amount
              </div>
              <div className="text-lg font-semibold">
                {formatCurrency(loanAmount)}
              </div>
            </div>
            <div>
              <div className="text-xs text-muted-foreground uppercase tracking-wide">
                Total Price Locked
              </div>
              <div className="text-lg font-semibold">
                {formatPoints(totalBuyPrice, 4)}
              </div>
            </div>
          </div>

          <Separator />

          {/* LO Commission - single row: Label | Percentage | Dollar Amount */}
          <div className="flex items-center justify-between py-2 bg-green-50 dark:bg-green-950/30 px-3 rounded-md">
            <div className="flex items-center gap-2">
              <span className="text-sm font-semibold text-green-800 dark:text-green-200">
                LO Commission
              </span>
              {compensation.planType === 'admin_entered' && (
                <Badge variant="outline" className="text-xs">
                  Admin Set
                </Badge>
              )}
            </div>
            <div className="flex items-center gap-6">
              <span className="text-sm font-medium text-green-600 dark:text-green-400">
                {compensation.planType === 'banked'
                  ? `${compensation.loCommissionPercent}%`
                  : `${formatPoints(compensation.loCommissionPercent, 3)}%`}
              </span>
              <span className="text-lg font-mono font-bold text-green-700 dark:text-green-300">
                {formatCurrency(compensation.loCommissionDollars)}
              </span>
            </div>
          </div>

          <Separator />

          {/* Lender Credit Section — informational display only. Does NOT
              reduce Net to LO (the LOCR above already accounted for any value
              released into lender-credit availability). Shown here so the LO
              can see the total issued credit on the loan for context. */}
          <div className="space-y-2">
            <div className="text-xs text-muted-foreground uppercase tracking-wide">
              Lender Credit Issued (informational)
            </div>
            <div className="flex items-center justify-between py-2 px-3 bg-muted/50 rounded-md">
              <span className="text-lg font-mono font-semibold">
                {formatCurrency(lenderCreditDollars)}
              </span>
              <span className="text-xs text-muted-foreground">
                Managed in Product &amp; Pricing
              </span>
            </div>
          </div>
        </CardContent>
      </Card>

      {/* Net to LO Summary - single row: Label | Percentage | Dollar Amount */}
      <Card className={cn(
        'border-2',
        compensation.netToLO >= 0
          ? 'border-green-500/50 bg-green-50/30 dark:bg-green-950/20'
          : 'border-red-500/50 bg-red-50/30 dark:bg-red-950/20'
      )}>
        <CardContent className="py-4">
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-3">
              <div className={cn(
                'p-2 rounded-full',
                compensation.netToLO >= 0
                  ? 'bg-green-100 dark:bg-green-900/50'
                  : 'bg-red-100 dark:bg-red-900/50'
              )}>
                <Wallet className={cn(
                  'h-5 w-5',
                  compensation.netToLO >= 0
                    ? 'text-green-600 dark:text-green-400'
                    : 'text-red-600 dark:text-red-400'
                )} />
              </div>
              <div className={cn(
                'text-sm font-bold uppercase tracking-wide',
                compensation.netToLO >= 0
                  ? 'text-green-800 dark:text-green-200'
                  : 'text-red-800 dark:text-red-200'
              )}>
                Net to LO
              </div>
            </div>
            <div className="flex items-center gap-6">
              <span className={cn(
                'text-sm font-medium',
                compensation.netToLO >= 0
                  ? 'text-green-600 dark:text-green-400'
                  : 'text-red-600 dark:text-red-400'
              )}>
                {loanAmount > 0 ? `${((compensation.netToLO / loanAmount) * 100).toFixed(3)}%` : '0.000%'}
              </span>
              <span className={cn(
                'text-2xl font-mono font-bold',
                compensation.netToLO >= 0
                  ? 'text-green-700 dark:text-green-300'
                  : 'text-red-700 dark:text-red-300'
              )}>
                {formatCurrency(compensation.netToLO)}
              </span>
            </div>
          </div>
        </CardContent>
      </Card>

      {/* Footer */}
      <div className="text-center text-xs text-muted-foreground pt-2">
        <p>Compensation calculated from Encompass pricing data</p>
      </div>

    </div>
  );
}

export default MyCompensationTab;
